import { useCallback, useEffect, useState } from 'react';
import { getLastErrors, getLastRequest } from '../lib/fetcher';
import type { ApiErrorInfo } from '../lib/api';
import { isLocalAppEnv } from '../lib/runtimeEnv';

export function Toast({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  useEffect(() => {
    const timer = window.setTimeout(onDismiss, 3000);
    return () => window.clearTimeout(timer);
  }, [onDismiss]);

  return (
    <div className="toast" onClick={onDismiss} role="status">
      {message}
    </div>
  );
}

export function LoadingSpinner() {
  return (
    <div className="loading">
      <div style={{
        width: 64,
        height: 64,
        borderRadius: 16,
        background: '#0f0618',
        border: '1px solid rgba(244,114,182,0.3)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        animation: 'pulse 2s ease-in-out infinite',
      }}>
        <svg viewBox="0 0 40 40" fill="none" width="40" height="40">
          <defs>
            <linearGradient id="ms_load" x1="10" y1="14" x2="30" y2="36" gradientUnits="userSpaceOnUse">
              <stop offset="0" stopColor="#7c3aed"/>
              <stop offset="0.5" stopColor="#a855f7"/>
              <stop offset="1" stopColor="#ec4899"/>
            </linearGradient>
          </defs>
          <path d="M11 13.5C13.5 10.5 17 9 20 9C23 9 26.5 10.5 29 13.5" stroke="url(#ms_load)" strokeWidth="2.2" strokeLinecap="round" fill="none"/>
          <path d="M14 16.5C15.8 14.5 17.8 13.5 20 13.5C22.2 13.5 24.2 14.5 26 16.5" stroke="url(#ms_load)" strokeWidth="2" strokeLinecap="round" fill="none" opacity="0.75"/>
          <path d="M17 19.5C18 18.2 19 17.7 20 17.7C21 17.7 22 18.2 23 19.5" stroke="url(#ms_load)" strokeWidth="1.7" strokeLinecap="round" fill="none" opacity="0.5"/>
          <path d="M20 34C20 34 8 26.5 8 20C8 16.5 10.8 14 14 14C16.3 14 18.3 15.2 20 17.2C21.7 15.2 23.7 14 26 14C29.2 14 32 16.5 32 20C32 26.5 20 34 20 34Z" fill="url(#ms_load)"/>
          <path d="M14 19C14.5 17 15.8 15.5 17 15C17.8 14.7 18.2 15.5 17.5 16.5C16.5 17.8 15.5 18.8 14 19Z" fill="rgba(255,255,255,0.28)"/>
        </svg>
      </div>
    </div>
  );
}

export function ErrorMessage({
  message,
  details,
  fetchInfo,
  onRetry,
}: {
  message: string;
  details?: string | null;
  fetchInfo?: ApiErrorInfo | null;
  onRetry?: () => void;
}) {
  const req = fetchInfo?.request ?? null;
  const dur = req?.durationMs ?? (req?.endTs && req?.startTs ? Math.max(0, req.endTs - req.startTs) : null);
  const startedAt = req?.startTs ? new Date(req.startTs).toLocaleString('ru') : null;

  const displayMessage =
    (message || '').toLowerCase().includes('failed to fetch') || (message || '').toLowerCase().includes('network request failed')
      ? 'Не удалось подключиться к серверу. Проверьте интернет и нажмите «Повторить».'
      : message;

  const hasDiagnostics = isLocalAppEnv() && (req || fetchInfo?.errorReportRequestId || details);

  return (
    <div className="error">
      <p>{displayMessage}</p>
      {onRetry && (
        <button className="btn btn-secondary" onClick={onRetry}>
          Повторить
        </button>
      )}
      {hasDiagnostics ? (
        <details className="diagnostics-panel">
          <summary>Подробности</summary>
          <div className="diagnostics-content">
            {req && (
              <p className="error-meta">
                <span className="diagnostics-key">request</span>{' '}
                <span className="diagnostics-value">
                  {req.method} {req.url} {startedAt ? `at ${startedAt}` : ''} dur={dur ?? '-'}ms status={fetchInfo?.status ?? '-'}
                </span>
              </p>
            )}
            {fetchInfo?.errorReportRequestId ? (
              <p className="error-meta">
                <span className="diagnostics-key">error_report_id</span>{' '}
                <span className="diagnostics-value">{fetchInfo.errorReportRequestId}</span>
              </p>
            ) : null}
            {details ? <pre className="diagnostics-echo-output">{details}</pre> : null}
          </div>
        </details>
      ) : null}
    </div>
  );
}

export function ApiUnavailablePanel({
  apiBaseUrl,
  errorInfo,
  onRetry,
}: {
  apiBaseUrl: string;
  errorInfo: ApiErrorInfo;
  onRetry: () => void;
}) {
  const [echoOriginResult, setEchoOriginResult] = useState<string | null>(null);
  const [echoOriginIsError, setEchoOriginIsError] = useState(false);
  const [echoOriginLoading, setEchoOriginLoading] = useState(false);
  const showLocalDiagnostics = isLocalAppEnv();

  const formatEchoOriginResponse = useCallback((text: string): string => {
    const trimmed = text.trim();
    if (!trimmed) return '';
    try {
      const parsed = JSON.parse(trimmed) as unknown;
      const redact = (value: unknown): unknown => {
        if (Array.isArray(value)) return value.map((item) => redact(item));
        if (value && typeof value === 'object') {
          const obj = value as Record<string, unknown>;
          const out: Record<string, unknown> = {};
          for (const [key, nested] of Object.entries(obj)) {
            const lower = key.toLowerCase();
            if (lower === 'initdata' || lower === 'authorization' || lower === 'cookie' || lower === 'cookies') {
              out[key] = '[redacted]';
              continue;
            }
            out[key] = redact(nested);
          }
          return out;
        }
        return value;
      };
      return JSON.stringify(redact(parsed), null, 2);
    } catch {
      return text;
    }
  }, []);

  const handleEchoOrigin = useCallback(async () => {
    if (echoOriginLoading) return;
    setEchoOriginLoading(true);
    setEchoOriginResult(null);
    setEchoOriginIsError(false);
    try {
      const response = await fetch(`${apiBaseUrl}/debug/echo`, {
        method: 'GET',
        credentials: 'omit',
        cache: 'no-store',
        referrerPolicy: 'no-referrer',
      });
      const text = await response.text();
      setEchoOriginResult(formatEchoOriginResponse(text));
      setEchoOriginIsError(false);
    } catch (err) {
      setEchoOriginResult(err instanceof Error ? err.message : String(err));
      setEchoOriginIsError(true);
    } finally {
      setEchoOriginLoading(false);
    }
  }, [apiBaseUrl, echoOriginLoading, formatEchoOriginResponse]);

  return (
    <div className="error api-unavailable">
      <p>API недоступен. Попробуйте ещё раз.</p>
      <button className="btn btn-secondary" onClick={onRetry}>
        Retry
      </button>
      {showLocalDiagnostics && (
        <details className="diagnostics-panel">
          <summary>Диагностика</summary>
          <div className="diagnostics-content">
            <div className="diagnostics-row">
              <span className="diagnostics-key">apiBaseUrl</span>
              <span className="diagnostics-value">{apiBaseUrl}</span>
            </div>
            <div className="diagnostics-row">
              <span className="diagnostics-key">error.kind</span>
              <span className="diagnostics-value">{errorInfo.kind}</span>
            </div>
            <div className="diagnostics-row">
              <span className="diagnostics-key">error.status</span>
              <span className="diagnostics-value">{errorInfo.status ?? '-'}</span>
            </div>
            <div className="diagnostics-row">
              <span className="diagnostics-key">error.message</span>
              <span className="diagnostics-value">{errorInfo.message}</span>
            </div>
            <div className="diagnostics-row">
              <span className="diagnostics-key">lastRequest</span>
              <pre className="diagnostics-echo-output">{JSON.stringify(getLastRequest(), null, 2) || '-'}</pre>
            </div>
            <div className="diagnostics-row">
              <span className="diagnostics-key">lastErrors</span>
              <pre className="diagnostics-echo-output">{JSON.stringify(getLastErrors().slice(0, 5), null, 2) || '-'}</pre>
            </div>
            <div className="diagnostics-row">
              <span className="diagnostics-key">debug.echo</span>
              <div className="diagnostics-value diagnostics-echo">
                <button className="btn btn-secondary diagnostics-btn" onClick={handleEchoOrigin} disabled={echoOriginLoading}>
                  Echo origin
                </button>
                {echoOriginResult !== null && (
                  <pre className={`diagnostics-echo-output${echoOriginIsError ? ' diagnostics-echo-output-error' : ''}`}>
                    {echoOriginResult}
                  </pre>
                )}
              </div>
            </div>
          </div>
        </details>
      )}
    </div>
  );
}

export function EmptyState() {
  return (
    <div className="empty-state">
      <p>У вас пока нет проектов</p>
      <p className="empty-hint">Создайте первый проект, чтобы начать</p>
    </div>
  );
}

export function EmptyPublicState() {
  return (
    <div className="empty-state">
      <p>Пока нет опубликованных проектов</p>
    </div>
  );
}
